import type { AspectRatioValue, CompetitorAspectRatioMix } from "@nexus/contracts";

export type AspectRatioMixEntity = "you" | "mainCompetitor" | "benchmark";
export type AspectRatioMixDisplay = "share" | "count";

export const ASPECT_RATIO_MIX_ENTITIES: readonly AspectRatioMixEntity[] = [
  "you",
  "mainCompetitor",
  "benchmark",
];

const PERCENT = 100;

/** One horizontal bar: an entity, with one stacked segment per aspect ratio. */
export type AspectRatioMixBar = { entity: AspectRatioMixEntity; total: number } & Record<
  AspectRatioValue,
  number
>;

/**
 * Turns the per-ratio counts into one bar per entity. `share` is each ratio's
 * percentage of that entity's creatives; an entity with no creatives gets 0s.
 */
export function buildAspectRatioMixBars(
  mix: CompetitorAspectRatioMix,
  display: AspectRatioMixDisplay,
): AspectRatioMixBar[] {
  return ASPECT_RATIO_MIX_ENTITIES.map((entity) => {
    const total = mix.totals[entity];
    const segments = Object.fromEntries(
      mix.rows.map((row) => [row.ratio, toDisplayValue(row[entity], total, display)]),
    ) as Record<AspectRatioValue, number>;
    return { entity, total, ...segments };
  });
}

function toDisplayValue(count: number, total: number, display: AspectRatioMixDisplay): number {
  if (display === "count") return count;
  return total > 0 ? (count / total) * PERCENT : 0;
}
